import { Fragment } from 'react';

import { Option } from '@/components/form/types';
import { Field, Model, PipelineStateRelation, Relation } from '@/explore/types';
import { getJoinKeys, getModelOrThrow } from '@/explore/model/utils';
import { getThroughRelations } from '@/explore/edit-pipeline/utils/relation';
import { findRelationJoinKey, getBaseStateOrThrow } from '@/explore/pipeline/state';

import { constructRelationOptionValue, getRelationTypeLabel } from './utils';

import styles from './select-options.module.scss';

const createRelationOption = (
  relation: PipelineStateRelation,
  relations: PipelineStateRelation[],
  fields: Field[],
  models: Model[],
): Option => {
  const baseModel = getModelOrThrow(models, relation.baseModelId);
  const relationsWithSameName = relations.filter((r) => r.name === relation.name);
  return {
    value: constructRelationOptionValue({
      key: relation.key,
      baseModelId: baseModel.modelId,
    }),
    label:
      relationsWithSameName.length > 1 ? `${relation.name} (on ${baseModel.name})` : relation.name,
    tooltip: (
      <RelationOptionTooltip
        relation={relation}
        relations={relations}
        fields={fields}
        models={models}
      />
    ),
  };
};

export const createRelationsOptions = (
  relations: PipelineStateRelation[],
  fields: Field[],
  models: Model[],
): Option[] =>
  relations.map((relation) => createRelationOption(relation, relations, fields, models));

interface RelationDescriptionProps {
  relation: PipelineStateRelation;
  relations: Relation[];
  fields: Field[];
  models: Model[];
  relatedEntityName: string;
  relatedFields: Field[];
  relatedEntityType?: 'model' | 'cell';
}

export const RelationDescription = (props: RelationDescriptionProps) => {
  const {
    relation,
    relations,
    fields,
    relatedFields,
    models,
    relatedEntityName,
    relatedEntityType = 'model',
  } = props;
  const { joinKeyOnBase, joinKeyOnRelated } = getJoinKeys(relation, relations, models);
  const joinFieldOnBase = findRelationJoinKey(fields, relation.baseModelId, joinKeyOnBase);
  const joinFieldOnRelated = findRelationJoinKey(relatedFields, relation.modelId, joinKeyOnRelated);
  const throughRelations =
    relation.joinStrategy !== undefined && 'steps' in relation.joinStrategy
      ? getThroughRelations(relation.joinStrategy, models, relations)
      : [];

  if (joinFieldOnBase === undefined || joinFieldOnRelated === undefined) {
    return null;
  }

  const renderThroughRelations = () => {
    return throughRelations.map((r, i) => (
      <Fragment key={i}>
        <span className={styles.identifier}>{r.name}</span>
        {i < throughRelations.length - 1 ? ' => ' : ''}
      </Fragment>
    ));
  };

  return (
    <p>
      Connects property <span className={styles.identifier}>{joinFieldOnBase.name}</span> to{' '}
      <span className={styles.identifier}>{joinFieldOnRelated.name}</span> on{' '}
      {relatedEntityType === 'cell' ? <>block</> : <>model</>}{' '}
      <span className={styles.identifier}>{relatedEntityName}</span>
      {throughRelations.length > 0 && <>through relations {renderThroughRelations()}</>}
    </p>
  );
};

interface RelationOptionTooltipProps {
  relation: PipelineStateRelation;
  relations: Relation[];
  fields: Field[];
  models: Model[];
}

export const RelationOptionTooltip = (props: RelationOptionTooltipProps) => {
  const { relation, relations, fields, models } = props;
  const model = getModelOrThrow(models, relation.modelId);
  const { fields: relatedFields } = getBaseStateOrThrow(relation.modelId, models);
  return (
    <aside className={styles.optionTooltip}>
      <strong className={styles.header}>
        {relation.name} <span className={styles.type}>{getRelationTypeLabel(relation.type)}</span>
      </strong>
      <p>
        <strong>Model:</strong> {model.name}
      </p>
      <RelationDescription
        relation={relation}
        relations={relations}
        fields={fields}
        models={models}
        relatedFields={relatedFields}
        relatedEntityName={model.name}
      />
    </aside>
  );
};
